import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { api } from '../../services/api';
import { fmtMoney, fmtHours, fmtNumber } from './format';
import RoleRankChart from './RoleRankChart';
import { c, tbl } from '../../theme';

// ── The role dimension ───────────────────────────────────────────────────────
//
// Cost center answers "which yard". This answers "which role, and is it direct
// or indirect" — the cut OT1 and OT2 both put at the bottom of their weekly
// view. It is one table with a grouping switch rather than four, because the
// two documents ask for the same table at different grains.
//
// The unmapped bucket is deliberately NOT sorted in with the families. It is
// not a job family, and a reader scanning the column would take it for one.
// It is also never dropped: a coverage gap that disappears from the table looks
// like less overtime rather than like a gap, which is the whole reason
// /api/job-profiles/coverage exists.

const GROUPINGS = [
  { key: 'group',   label: 'Family group', heading: 'Job family group' },
  { key: 'family',  label: 'Family',  heading: 'Job family' },
  { key: 'category',label: 'Category',heading: 'Labor category' },
  { key: 'class',   label: 'Class',   heading: 'Labor class' },
  { key: 'profile', label: 'Profile', heading: 'Job profile' },
];

// The classification is HR's own, from the Job Profile Classification Bible --
// no longer a default this project drafted. What is still outstanding is the
// RELEASE: Operations and Finance have not confirmed publishing it, so the
// figures are sound but not yet sanctioned, and the note says which of the two
// it is rather than implying the numbers are guesses.
const CLASS_LABELS = { direct: 'Direct', indirect: 'Indirect' };

export default function ByRolePanel({ scopeParams, filterQ = '', defaultBy = 'group', ccId = null }) {
  // The level decides what this table opens on: an executive is asking about
  // the direct/indirect split, a site manager about which families are running
  // the hours. Both can switch; only the starting point differs.
  const [by, setBy] = useState(defaultBy);
  // Which family is open. Only families drill: a family is a set of job
  // profiles, so opening one has somewhere to go. Group and category are cuts of
  // the same rows rather than containers, and class has two values -- expanding
  // any of those would just restate the switch above.
  const [openFamily, setOpenFamily] = useState(null);
  const drillable = by === 'family';

  const ccQ = ccId ? `&cc_id=${encodeURIComponent(ccId)}` : '';
  const qs = scopeParams ? `${scopeParams}&by=${by}${filterQ}${ccQ}` : null;

  const pickBy = (next) => { setBy(next); setOpenFamily(null); };

  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-by-role', qs],
    queryFn:  () => api.get(`/overtime/by-role?${qs}`),
    enabled:  !!qs,
  });

  const rows       = data?.rows || [];
  const unmapped   = data?.unmapped || null;
  // Scoped to what this request returned, so releasing part of the bible clears
  // the caveat for the cuts that were signed off and leaves it on the rest.
  const unreleased = data?.unreleased || null;
  const heading  = GROUPINGS.find((g) => g.key === by)?.heading || 'Role';

  return (
    <>
      <div style={s.head}>
        <div style={s.title}>By role</div>
        <div style={s.seg}>
          {GROUPINGS.map((g) => (
            <button key={g.key} type="button" onClick={() => pickBy(g.key)}
                    style={{ ...s.segBtn, ...(by === g.key ? s.segOn : null) }}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {(by === 'class' || by === 'category') && (
        <div style={s.draftNote}>
          Classification comes from HR's Job Profile Classification Bible.
          {by === 'class'
            ? ' Class is the direct / not-direct rollup of the four labor categories — switch to Category to see indirect and SG&A separately.'
            : ' Direct, Indirect, G&A and Sales as HR classifies them.'}
          {/* Said only while it is true. A fixed sentence told every reader the
              classification was unreleased however much of it had been signed
              off, which made releasing it invisible and the caveat unfalsifiable. */}
          {unreleased?.profiles > 0 && (
            <> {fmtNumber(unreleased.profiles)} job profile{unreleased.profiles === 1 ? '' : 's'} here,
              carrying {fmtMoney(unreleased.ot_amount)} ({unreleased.share}% of this view), rest on a
              classification Operations and Finance have not yet released for publication.</>
          )}
        </div>
      )}

      {/* The chart ranks; the table gives exact values and the other columns.
          Same response, so they cannot disagree. */}
      {!isLoading && !error && (rows.length > 0 || unmapped) && (
        <RoleRankChart rows={rows} unmapped={unmapped} heading={heading} />
      )}

      <div style={s.card}>
        {isLoading ? (
          <div style={s.empty}>Loading…</div>
        ) : error ? (
          <div style={s.empty}>{String(error.message || error)}</div>
        ) : !rows.length && !unmapped ? (
          <div style={s.empty}>No overtime in this period.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {drillable && <th style={{ ...tbl.th, width: '28px' }} />}
                <th style={tbl.th}>{heading}</th>
                <th style={tbl.thR}>Employees</th>
                <th style={tbl.thR}>Profiles</th>
                <th style={tbl.thR}>OT hours</th>
                <th style={tbl.thR}>OT cost</th>
                <th style={tbl.thR}>Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = drillable && openFamily === row.key;
                return (
                  <Fragment key={row.key}>
                    <tr style={drillable ? s.rowDrill : tbl.tr}
                        onClick={drillable ? () => setOpenFamily(open ? null : row.key) : undefined}>
                      {drillable && (
                        <td style={s.tdChev}>
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                      )}
                      <td style={s.tdName}>{CLASS_LABELS[row.key] || row.key}</td>
                      <td style={tbl.tdR}>{fmtNumber(row.employee_count)}</td>
                      <td style={tbl.tdR}>{fmtNumber(row.profile_count)}</td>
                      <td style={tbl.tdR}>{fmtHours(row.ot_hours)}</td>
                      <td style={s.tdRb}>{fmtMoney(row.ot_amount)}</td>
                      <td style={tbl.tdR}>{row.share_amount}%</td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={7} style={s.drillCell}>
                          <FamilyProfiles scopeParams={scopeParams} filterQ={filterQ}
                                          ccId={ccId} family={row.key} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

              {/* Held out of the ranking on purpose — see the note at the top. */}
              {unmapped && (
                <tr style={s.unmappedRow}>
                  {drillable && <td style={s.tdChev} />}
                  <td style={s.tdUnmapped}>
                    <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
                    Not in the mapping
                  </td>
                  <td style={tbl.tdR}>{fmtNumber(unmapped.employee_count)}</td>
                  <td style={tbl.tdR}>{fmtNumber(unmapped.profile_count)}</td>
                  <td style={tbl.tdR}>{fmtHours(unmapped.ot_hours)}</td>
                  <td style={s.tdRb}>{fmtMoney(unmapped.ot_amount)}</td>
                  <td style={tbl.tdR}>{unmapped.share_amount}%</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {unmapped && (
        <div style={s.gapNote}>
          {fmtNumber(unmapped.profile_count)} job profile{unmapped.profile_count === 1 ? '' : 's'} in
          this payroll {unmapped.profile_count === 1 ? 'has' : 'have'} no entry in the mapping, so
          {' '}{fmtMoney(unmapped.ot_amount)} sits outside every family and class figure above.
          Add them under Mappings → Job Profiles.
        </div>
      )}

      {data?.role?.excludes_unmatched && (
        <div style={s.gapNote}>
          A role filter is applied, so these totals exclude any profile the mapping
          does not carry. They are legitimately smaller than the unfiltered board.
        </div>
      )}
    </>
  );
}

// ── The profiles inside one family ───────────────────────────────────────────
//
// The same endpoint, filtered to the family and grouped by profile, so the
// drill and the row it opened from cannot disagree: the profiles sum to the
// family because it is one query answered twice at different grains.
//
// Shares here are OF THE FAMILY, not of the board, and the column says so. A
// profile that is 40% of Container Maintenance is a fraction of a percent of
// the company, and printing the company figure inside a family's own drill
// would make every row in it look like nothing.
function FamilyProfiles({ scopeParams, filterQ = '', ccId, family }) {
  const ccQ = ccId ? `&cc_id=${encodeURIComponent(ccId)}` : '';
  const qs  = `${scopeParams}&by=profile&family=${encodeURIComponent(family)}${filterQ}${ccQ}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-by-role-drill', qs],
    queryFn:  () => api.get(`/overtime/by-role?${qs}`),
  });

  const rows = data?.rows || [];

  if (isLoading) return <div style={s.drillEmpty}>Loading…</div>;
  if (error)     return <div style={s.drillEmpty}>{String(error.message || error)}</div>;
  if (!rows.length) return <div style={s.drillEmpty}>No job profile in this family carried overtime here.</div>;

  return (
    <table style={s.subTable}>
      <thead>
        <tr>
          <th style={tbl.thSm}>Job profile</th>
          <th style={tbl.thRSm}>Employees</th>
          <th style={tbl.thRSm}>OT hours</th>
          <th style={tbl.thRSm}>OT cost</th>
          <th style={tbl.thRSm}>Share of family</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} style={tbl.tr}>
            <td style={tbl.tdSm}>{r.key}</td>
            <td style={tbl.tdRSm}>{fmtNumber(r.employee_count)}</td>
            <td style={tbl.tdRSm}>{fmtHours(r.ot_hours)}</td>
            <td style={tbl.tdRSm}>{fmtMoney(r.ot_amount)}</td>
            <td style={tbl.tdRSm}>{r.share_amount}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const s = {
  head:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
               gap: '12px', margin: '26px 0 10px', flexWrap: 'wrap' },
  title:     { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
               letterSpacing: '0.5px', color: c.textMuted },
  seg:       { display: 'flex', border: `1px solid ${c.borderStrong}`, borderRadius: '8px',
               overflow: 'hidden', background: c.surface },
  segBtn:    { appearance: 'none', border: 'none', background: 'transparent', cursor: 'pointer',
               padding: '6px 12px', fontSize: '12px', fontFamily: 'inherit', color: c.textStrong,
               borderRight: `1px solid ${c.border}` },
  segOn:     { background: c.navy, color: c.onNavy, fontWeight: 600 },
  card:      { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tdName:    { padding: '9px 14px', color: c.text },
  tdRb:      { padding: '9px 14px', color: c.text, textAlign: 'right', fontWeight: 600,
               fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  rowDrill:  { borderBottom: `1px solid ${c.canvas}`, cursor: 'pointer' },
  tdChev:    { padding: '9px 0 9px 12px', color: c.textFaint, width: '28px', verticalAlign: 'middle' },
  drillCell: { padding: '0 14px 12px 40px', background: c.canvas },
  subTable:  { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  drillEmpty:{ padding: '12px 2px', color: c.textFaint, fontSize: '12px' },
  unmappedRow: { borderBottom: `1px solid ${c.canvas}`, background: c.warningTint },
  tdUnmapped:  { padding: '9px 14px', color: c.warningText, fontWeight: 600 },
  empty:     { textAlign: 'center', padding: '28px', color: c.textFaint },
  draftNote: { background: c.infoTint, border: `1px solid ${c.border}`, borderRadius: '8px',
               padding: '8px 12px', fontSize: '12px', color: c.textStrong, marginBottom: '10px' },
  gapNote:   { background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: '8px',
               padding: '8px 12px', fontSize: '12px', color: c.textStrong, marginTop: '10px' },
};
